import { SnippetRenderer, SnippetsWrapper } from '@grofers/react';
import React from 'react';
import { useState, useRef, useEffect } from 'react';
import SnippetizedList from './SnippetizedList';
import {
  getHeaderSnippet,
  getTimelineSnippet,
  getMapData,
  transformToCrystalResponse,
} from '../../../utils/crystalTransformer/responseUtils';
import CrystalMapWrapper from './CrystalMap/CrystalMapWrapper';
import CrystalMap from './CrystalMap/CrystalMap';
import { EmptyState } from '../../commons';
import { InvalidSearchImg } from '../../../images/images';
import { Loader } from '../../../UiKit';
import { useQueryCall } from '../../../api/react-client';
import { lifelineEndPoints } from '../../../api/end-points';

const TRACK_ORDER_STATE_FOR_ORDER_STATE_MAP = [
  'DELIVERED',
  'CANCELLED',
  'DELIVERY_FAILED',
];

export const CrystalView = (props) => {
  const [{ status, response, errorMessage }, setResponse] = useState({
    status: 'idle',
    response: null,
    errorMessage: null,
  });
  const [trackOrderSnippets, setTrackOrderSnippets] = useState([]);
  const [headerSnippet, setHeaderSnippet] = useState({});
  const [timelineSnippet, setTimelineSnippet] = useState({});
  const [headerSnippetHeight, setHeaderSnippetHeight] = useState(0);
  const [headerSnippetMarginBottom, setHeaderSnippetMarginBottom] = useState(0);
  const [mapData, setMapData] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(0);
  const snippetsListRef = useRef();
  const refreshIntervalIdRef = useRef(null);
  const getCrystalViewEndPoint = `${lifelineEndPoints.GET_CRYSTAL_VIEW}?order_id=${props.orderId}`;

  const {
    data: crystalData,
    isLoading,
    refetch: getCrystalDetails,
  } = useQueryCall({}, getCrystalViewEndPoint, [props.orderId]);

  useEffect(() => {
    if (crystalData?.isError) {
      setResponse({ status: 'error', errorMessage: crystalData?.message });
    } else {
      setResponse({ status: 'success', response: crystalData });
      setRefreshInterval(crystalData?.refresh_interval);
    }
  }, [crystalData]);

  const fetchTrackOrderResult = () => {
    getCrystalDetails(props.orderId);
  };

  useEffect(() => {
    if (refreshIntervalIdRef.current)
      clearInterval(refreshIntervalIdRef.current);

    if (!props.isLiveOrder || refreshInterval === 0) return;
    let intervalId = setInterval(fetchTrackOrderResult, refreshInterval);
    refreshIntervalIdRef.current = intervalId;
  }, [fetchTrackOrderResult, props.isLiveOrder, refreshInterval]);

  useEffect(() => {
    if (response) {
      let orderState = response.page_meta?.custom_data?.order_state;
      if (TRACK_ORDER_STATE_FOR_ORDER_STATE_MAP.includes(orderState)) {
        setHeaderSnippetHeight(68);
        setHeaderSnippetMarginBottom(10);
      } else {
        setHeaderSnippetHeight(165);
      }
      let headerSnippetHeight = TRACK_ORDER_STATE_FOR_ORDER_STATE_MAP.includes(
        orderState,
      )
        ? 68
        : 165;
      const headerSnippet = getHeaderSnippet(response);
      const bffResponse = transformToCrystalResponse(response);
      const mapData = getMapData(response);
      const timelineSnippet = getTimelineSnippet(response);
      setTrackOrderSnippets(bffResponse.response);
      setHeaderSnippet(headerSnippet);
      setMapData(mapData);
      setTimelineSnippet(timelineSnippet);
      setHeaderSnippetHeight(headerSnippetHeight);
    }
  }, [response]);

  useEffect(() => {
    fetchTrackOrderResult();
    return () => {
      if (refreshIntervalIdRef.current) {
        clearInterval(refreshIntervalIdRef.current);
      }
    };
  }, []);

  if (isLoading)
    return (
      <div className='tw-h-[70vh] tw-flex tw-justify-center tw-items-center'>
        <Loader />
      </div>
    );

  if (status === 'error' || !crystalData)
    return (
      <div className='tw-h-[70vh] tw-flex tw-justify-center tw-items-center'>
        <EmptyState img={InvalidSearchImg} heading={errorMessage} />
      </div>
    );

  return (
    <div
      style={{ background: trackOrderSnippets.layout_config?.bg_color_hex }}
      data-dd-privacy='mask'
    >
      <div
        style={{
          height: `${headerSnippetHeight}px`,
          marginBottom: `${headerSnippetMarginBottom}px`,
          position: 'sticky',
          top: '0px',
          zIndex: 10,
        }}
      >
        <SnippetsWrapper>
          <SnippetRenderer {...headerSnippet}></SnippetRenderer>
        </SnippetsWrapper>
      </div>
      <div>
        <SnippetsWrapper>
          <SnippetRenderer {...timelineSnippet}></SnippetRenderer>
        </SnippetsWrapper>
      </div>

      {mapData ? (
        <div style={{ position: 'relative', width: '100%', height: '350px' }}>
          <CrystalMapWrapper>
            <CrystalMap {...mapData} orderBasicInfo={props?.orderBasicInfo} />
          </CrystalMapWrapper>
        </div>
      ) : null}
      <div
        ref={snippetsListRef}
        className='snippets-list-container'
        style={{
          position: 'relative',
          zIndex: 1,
          ...(mapData && { boxShadow: '0px -20px 20px 0px #E8E8E8' }),
        }}
        css={`
          div[role='button'],
          button {
            cursor: default !important;
          }
        `}
      >
        <SnippetizedList {...trackOrderSnippets} />
      </div>
    </div>
  );
};
